import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence, useInView } from 'framer-motion'
import {
  Globe,
  Settings,
  Cog,
  Box,
  Factory,
  Wrench,
  Flame,
  Layers,
  CircleDot,
  GitBranch,
  Database,
  Link2,
  Triangle,
  Split,
  Workflow,
  Cpu,
  Ruler,
  BoxSelect,
  X,
  ChevronLeft,
  ChevronRight,
  Images,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface ProductItem {
  key: string
  images: string[]
}

interface ProductCategory {
  key: string
  icon: React.ElementType
  color: string
  items: ProductItem[]
}

const productCategories: ProductCategory[] = [
  {
    key: 'produzioneEstera',
    icon: Globe,
    color: 'from-[#FAB529] to-[#F7BC37]',
    items: [
      {
        key: 'steelCastings',
        images: [
          '/images/products/produzione_estera/fusioni_acciaio_1.jpg',
          '/images/products/produzione_estera/fusioni_acciaio_2.jpg',
          '/images/products/produzione_estera/fusioni_acciaio_3.jpg',
        ],
      },
      {
        key: 'aluminumCastings',
        images: [
          '/images/products/produzione_estera/fusioni_alluminio_1.jpg',
          '/images/products/produzione_estera/fusioni_alluminio_2.jpg',
          '/images/products/produzione_estera/fusioni_alluminio_3.jpg',
        ],
      },
      {
        key: 'castIron',
        images: [
          '/images/products/produzione_estera/fusioni_ghisa_1.jpg',
          '/images/products/produzione_estera/fusioni_ghisa_2.jpg',
          '/images/products/produzione_estera/fusioni_ghisa_3.jpg',
        ],
      },
      {
        key: 'turningMilling',
        images: [
          '/images/products/produzione_estera/fusioni_acciaio_1.jpg',
          '/images/products/produzione_estera/fusioni_acciaio_2.jpg',
        ],
      },
      {
        key: 'sheetMetalStamping',
        images: [
          '/images/products/produzione_estera/stampaggio_lamiera_1.jpg',
          '/images/products/produzione_estera/stampaggio_lamiera_2.jpg',
          '/images/products/produzione_estera/stampaggio_lamiera_3.jpg',
        ],
      },
      {
        key: 'transmissionComponents',
        images: [
          '/images/products/organi_trasmissione/ingranaggi_1.jpg',
          '/images/products/organi_trasmissione/ingranaggi_2.jpg',
          '/images/products/organi_trasmissione/ingranaggi_3.jpg',
        ],
      },
      {
        key: 'forging',
        images: [
          '/images/products/produzione_estera/forgiatura_1.jpg',
          '/images/products/produzione_estera/forgiatura_2.jpg',
          '/images/products/produzione_estera/forgiatura_3.jpg',
        ],
      },
      {
        key: 'plasticMolding',
        images: [
          '/images/products/produzione_estera/stampaggio_plastiche_1.jpg',
          '/images/products/produzione_estera/stampaggio_plastiche_2.jpg',
          '/images/products/produzione_estera/stampaggio_plastiche_3.jpg',
        ],
      },
      {
        key: 'bronzeBrass',
        images: [
          '/images/products/produzione_estera/bronzo_ottone_1.jpg',
          '/images/products/produzione_estera/bronzo_ottone_2.jpg',
          '/images/products/produzione_estera/bronzo_ottone_3.jpg',
        ],
      },
    ],
  },
  {
    key: 'lavorazioniMeccaniche',
    icon: Settings,
    color: 'from-[#F7BC37] to-[#FAB529]',
    items: [
      {
        key: 'turningMilling',
        images: [
          '/images/products/lavorazioni_meccaniche/tornitura_fresatura_1.jpg',
          '/images/products/lavorazioni_meccaniche/tornitura_fresatura_2.jpg',
          '/images/products/lavorazioni_meccaniche/tornitura_fresatura_3.jpg',
        ],
      },
      {
        key: 'carpentry',
        images: [
          '/images/products/lavorazioni_meccaniche/carpenteria_1.jpg',
          '/images/products/lavorazioni_meccaniche/carpenteria_2.jpg',
          '/images/products/lavorazioni_meccaniche/carpenteria_3.jpg',
        ],
      },
    ],
  },
  {
    key: 'organiTrasmissione',
    icon: Cog,
    color: 'from-[#FAB529] to-[#FFD980]',
    items: [
      {
        key: 'gearsPulleys',
        images: [
          '/images/products/organi_trasmissione/ingranaggi_1.jpg',
          '/images/products/organi_trasmissione/ingranaggi_2.jpg',
          '/images/products/organi_trasmissione/ingranaggi_3.jpg',
        ],
      },
      {
        key: 'rollers',
        images: [
          '/images/products/organi_trasmissione/cuscinetti_1.jpg',
          '/images/products/organi_trasmissione/cuscinetti_2.jpg',
          '/images/products/organi_trasmissione/cuscinetti_3.jpg',
        ],
      },
      {
        key: 'shafts',
        images: [
          '/images/products/organi_trasmissione/ingranaggi_1.jpg',
          '/images/products/organi_trasmissione/ingranaggi_2.jpg',
        ],
      },
      {
        key: 'bearingsSupports',
        images: [
          '/images/products/organi_trasmissione/cuscinetti_1.jpg',
          '/images/products/organi_trasmissione/cuscinetti_2.jpg',
          '/images/products/organi_trasmissione/cuscinetti_3.jpg',
        ],
      },
      {
        key: 'chains',
        images: [
          '/images/products/organi_trasmissione/ingranaggi_1.jpg',
          '/images/products/organi_trasmissione/ingranaggi_2.jpg',
        ],
      },
      {
        key: 'conveyorBelts',
        images: [
          '/images/products/organi_trasmissione/cuscinetti_1.jpg',
          '/images/products/organi_trasmissione/cuscinetti_2.jpg',
        ],
      },
      {
        key: 'joints',
        images: [
          '/images/products/organi_trasmissione/ingranaggi_1.jpg',
          '/images/products/organi_trasmissione/ingranaggi_2.jpg',
        ],
      },
      {
        key: 'gearboxes',
        images: [
          '/images/products/organi_trasmissione/cuscinetti_1.jpg',
          '/images/products/organi_trasmissione/cuscinetti_2.jpg',
          '/images/products/organi_trasmissione/cuscinetti_3.jpg',
        ],
      },
    ],
  },
  {
    key: 'materiePlastiche',
    icon: Box,
    color: 'from-[#FFD980] to-[#FAB529]',
    items: [
      {
        key: 'cncMachining',
        images: [
          '/images/products/materie_plastiche/lavorazioni_cnc_1.jpg',
          '/images/products/materie_plastiche/lavorazioni_cnc_2.jpg',
          '/images/products/materie_plastiche/lavorazioni_cnc_3.jpg',
        ],
      },
      {
        key: 'semiFinished',
        images: [
          '/images/products/materie_plastiche/lavorazioni_cnc_1.jpg',
          '/images/products/materie_plastiche/lavorazioni_cnc_2.jpg',
        ],
      },
      {
        key: 'injectionMolding',
        images: [
          '/images/products/materie_plastiche/stampaggio_iniezione_1.jpg',
          '/images/products/materie_plastiche/stampaggio_iniezione_2.jpg',
          '/images/products/materie_plastiche/stampaggio_iniezione_3.jpg',
        ],
      },
    ],
  },
]

const itemIcons: Record<string, React.ElementType> = {
  steelCastings: Factory,
  aluminumCastings: Box,
  castIron: Flame,
  turningMilling: Settings,
  sheetMetalStamping: Layers,
  transmissionComponents: Cog,
  forging: Flame,
  plasticMolding: BoxSelect,
  bronzeBrass: Flame,
  carpentry: Wrench,
  gearsPulleys: Cog,
  rollers: CircleDot,
  shafts: GitBranch,
  bearingsSupports: Database,
  chains: Link2,
  conveyorBelts: Triangle,
  joints: Split,
  gearboxes: Workflow,
  cncMachining: Cpu,
  semiFinished: Ruler,
  injectionMolding: BoxSelect,
}

export function ProductsTabs() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)
  const [selectedProduct, setSelectedProduct] = useState<ProductItem | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const activeCategory = productCategories[activeTab]
  const ActiveIcon = activeCategory.icon

  const openProductModal = (product: ProductItem) => {
    setSelectedProduct(product)
    setCurrentImageIndex(0)
  }

  const closeModal = () => {
    setSelectedProduct(null)
    setCurrentImageIndex(0)
  }

  const nextImage = () => {
    if (selectedProduct) {
      setCurrentImageIndex((prev) =>
        prev === selectedProduct.images.length - 1 ? 0 : prev + 1
      )
    }
  }

  const prevImage = () => {
    if (selectedProduct) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProduct.images.length - 1 : prev - 1
      )
    }
  }

  const getItemName = (key: string) => t(`method.products.items.${key}`)

  return (
    <section className="py-16 md:py-24 bg-gray-50/50" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('method.products.categoryTitle')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('method.products.categorySubtitle')}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-2 mb-8"
          role="tablist"
        >
          {productCategories.map((category, index) => {
            const Icon = category.icon
            const isActive = index === activeTab
            return (
              <button
                key={category.key}
                onClick={() => setActiveTab(index)}
                role="tab"
                aria-selected={isActive}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-300',
                  isActive
                    ? 'bg-white shadow-md text-gray-900 ring-2 ring-primary/20'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                )}
              >
                <Icon className={cn('w-5 h-5', isActive ? 'text-primary' : 'text-gray-400')} />
                <span className="hidden sm:inline">
                  {t(`method.products.categories.${category.key}.title`)}
                </span>
                <span className="sm:hidden">
                  {t(`method.products.categories.${category.key}.title`).split(' ')[0]}
                </span>
              </button>
            )
          })}
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            role="tabpanel"
            className="bg-white rounded-2xl shadow-lg overflow-hidden"
          >
            {/* Header */}
            <div
              className={cn(
                'px-6 py-6 bg-gradient-to-r',
                activeCategory.color
              )}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <ActiveIcon className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    {t(`method.products.categories.${activeCategory.key}.title`)}
                  </h3>
                  <p className="text-white/80 text-sm mt-1">
                    {activeCategory.items.length} {t('method.products.productsAvailable')}
                  </p>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeCategory.items.map((item, idx) => {
                  const ItemIcon = itemIcons[item.key] || CircleDot
                  const hasImages = item.images.length > 0
                  const itemName = getItemName(item.key)
                  return (
                    <motion.div
                      key={item.key}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                      onClick={() => hasImages && openProductModal(item)}
                      className={cn(
                        'flex items-start gap-3 p-4 rounded-xl',
                        'bg-gray-50 hover:bg-gray-100 transition-colors duration-200',
                        'border border-gray-100',
                        hasImages && 'cursor-pointer group'
                      )}
                    >
                      <div
                        className={cn(
                          'w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center'
                        )}
                        style={{
                          background: 'linear-gradient(135deg, rgba(250,181,41,0.15) 0%, rgba(247,188,55,0.15) 100%)',
                        }}
                      >
                        <ItemIcon
                          className="w-5 h-5 text-[#FAB529]"
                          strokeWidth={1.5}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-700 font-medium leading-tight pt-2 block">
                          {itemName}
                        </span>
                        {hasImages && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500 group-hover:text-[#FAB529] transition-colors">
                            <Images className="w-3 h-3" />
                            <span>{item.images.length} {t('method.products.photos')}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 text-center"
        >
          <p className="text-sm text-gray-500">
            {t('method.products.footerNote')}
          </p>
        </motion.div>
      </div>

      {/* Product Image Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">
                  {getItemName(selectedProduct.key)}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Image Gallery */}
              <div className="relative aspect-[16/10] bg-gray-900">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={selectedProduct.images[currentImageIndex]}
                    alt={`${getItemName(selectedProduct.key)} - ${currentImageIndex + 1}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full h-full object-contain"
                  />
                </AnimatePresence>

                {/* Navigation Arrows */}
                {selectedProduct.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all hover:scale-110"
                    >
                      <ChevronLeft className="w-6 h-6 text-gray-900" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all hover:scale-110"
                    >
                      <ChevronRight className="w-6 h-6 text-gray-900" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/60 text-white text-sm font-medium">
                  {currentImageIndex + 1} / {selectedProduct.images.length}
                </div>
              </div>

              {/* Thumbnails */}
              {selectedProduct.images.length > 1 && (
                <div className="p-4 bg-gray-50">
                  <div className="flex gap-2 justify-center">
                    {selectedProduct.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={cn(
                          'w-16 h-16 rounded-lg overflow-hidden border-2 transition-all',
                          idx === currentImageIndex
                            ? 'border-[#FAB529] ring-2 ring-[#FAB529]/20'
                            : 'border-transparent opacity-60 hover:opacity-100'
                        )}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
